import { Head, Link } from "@inertiajs/react"
import {
  ArrowRight,
  ArrowUpDown,
  FileStack,
  ImageIcon,
  Lock,
  PenLine,
  Scissors,
} from "lucide-react"

import { EmptyState } from "@/components/dashboard/empty-state"
import { RecentDocumentsTable } from "@/components/dashboard/recent-documents-table"
import { UsageMeter } from "@/components/dashboard/usage-meter"
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import {
  dashboardPath,
  documentsPath,
  imageToPdfPath,
  mergePdfPath,
  protectPdfPath,
  reorderPdfPath,
  signPdfPath,
  splitPdfPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface Document {
  id: number
  filename: string
  operation_type: string | null
  status: "pending" | "processing" | "completed" | "failed"
  size: number | null
  created_at: string
  expires_at: string | null
  expired: boolean
  download_url: string | null
}

interface DashboardProps {
  documents: Document[]
  usage: {
    used: number
    limit: number
    plan: "free" | "plus" | "pro"
  }
  plan: "free" | "plus" | "pro"
}

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: dashboardPath(),
  },
]

const quickActions = [
  {
    title: "Merge PDFs",
    description: "Combine multiple PDFs into one",
    href: mergePdfPath(),
    icon: FileStack,
  },
  {
    title: "Split PDF",
    description: "Extract pages from a PDF",
    href: splitPdfPath(),
    icon: Scissors,
  },
  {
    title: "Reorder Pages",
    description: "Rearrange PDF pages",
    href: reorderPdfPath(),
    icon: ArrowUpDown,
  },
  {
    title: "Protect PDF",
    description: "Add password protection",
    href: protectPdfPath(),
    icon: Lock,
  },
  {
    title: "Sign PDF",
    description: "Add your signature",
    href: signPdfPath(),
    icon: PenLine,
  },
  {
    title: "Image to PDF",
    description: "Convert images to PDF",
    href: imageToPdfPath(),
    icon: ImageIcon,
  },
]

export default function Dashboard({ documents, usage, plan }: DashboardProps) {
  const hasDocuments = documents.length > 0

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <div className="flex h-full flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4">
          <div className="flex flex-col gap-6">
            <UsageMeter
              used={usage.used}
              limit={usage.limit}
              plan={usage.plan}
            />

            {hasDocuments ? (
              <>
                <section>
                  <h2 className="mb-4 text-lg font-semibold">Quick Actions</h2>
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
                    {quickActions.map((action) => (
                      <Link key={action.href} href={action.href} prefetch>
                        <Card className="hover:bg-muted/50 h-full transition-colors">
                          <CardHeader className="p-4">
                            <action.icon className="text-primary mb-2 h-6 w-6" />
                            <CardTitle className="text-sm">
                              {action.title}
                            </CardTitle>
                            <CardDescription className="text-xs">
                              {action.description}
                            </CardDescription>
                          </CardHeader>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </section>

                <section>
                  <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Recent Documents</h2>
                    <Link
                      href={documentsPath()}
                      className="text-brand hover:text-brand/80 flex items-center gap-1 text-sm font-medium"
                    >
                      View all documents
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                  <RecentDocumentsTable documents={documents} plan={plan} />
                </section>
              </>
            ) : (
              <EmptyState />
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
